/**
 * Minimal SSE client for Supabase Edge functions that stream typed events.
 *
 * The native `EventSource` API only supports GET. We POST with an Authorization header
 * and read the response body as a stream, parsing the SSE wire format ourselves:
 *
 *   event: <name>\n
 *   data: <json or text>\n\n
 */

import { supabase, supabaseUrl } from './supabase';

export type SseEventHandler = (eventName: string, data: unknown) => void;

export interface InvokeStreamParams {
  /** Edge function name, e.g. "generate-flow". */
  functionName?: string;
  /** Absolute endpoint URL for non-Supabase workers. */
  url?: string;
  /** JSON body to POST. */
  body: unknown;
  /** Called for each typed event. */
  onEvent: SseEventHandler;
  /** Optional abort signal. */
  signal?: AbortSignal;
}

const tryParseJson = (raw: string): unknown => {
  if (raw.length === 0) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
};

/**
 * POSTs to a Supabase edge function with `Accept: text/event-stream` and parses streamed
 * SSE events. Resolves when the server closes the stream. Throws on network/auth errors.
 */
export const invokeEdgeFunctionStream = async (params: InvokeStreamParams): Promise<void> => {
  const { data: { session } } = await supabase.auth.getSession();
  const accessToken = session?.access_token;
  if (!accessToken) {
    throw new Error('Session expirée. Reconnecte-toi.');
  }

  const url = params.url ?? (params.functionName ? `${supabaseUrl}/functions/v1/${params.functionName}` : null);
  if (!url) {
    throw new Error('Endpoint SSE manquant.');
  }

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      authorization: `Bearer ${accessToken}`,
      'content-type': 'application/json',
      accept: 'text/event-stream',
    },
    body: JSON.stringify(params.body),
    signal: params.signal,
  });

  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}`;
    try {
      const text = await response.text();
      const parsed = tryParseJson(text);
      if (typeof parsed === 'object' && parsed && 'error' in parsed) {
        const err = (parsed as { error: unknown }).error;
        if (typeof err === 'string') errorMessage = err;
        else if (typeof err === 'object' && err && 'message' in err) {
          errorMessage = String((err as { message: unknown }).message);
        }
      } else if (typeof parsed === 'string') {
        errorMessage = parsed;
      }
    } catch {
      // keep generic message
    }
    throw new Error(errorMessage);
  }

  if (!response.body) {
    throw new Error('Stream indisponible.');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  const dispatchEvent = (rawBlock: string): boolean => {
    const lines = rawBlock.split('\n');
    let eventName = 'message';
    const dataLines: string[] = [];
    for (const line of lines) {
      if (line.startsWith(':')) continue; // SSE comment
      if (line.startsWith('event:')) {
        eventName = line.slice(6).trim();
        continue;
      }
      if (line.startsWith('data:')) {
        dataLines.push(line.slice(5).trimStart());
        continue;
      }
    }
    if (dataLines.length === 0) return false;
    const data = tryParseJson(dataLines.join('\n'));
    params.onEvent(eventName, data);
    return eventName === 'complete' || eventName === 'error';
  };

  let terminalEventReceived = false;
  while (!terminalEventReceived) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    // SSE event delimiter is a blank line (\n\n). Split greedy, keep the trailing partial.
    let separatorIndex = buffer.indexOf('\n\n');
    while (separatorIndex !== -1) {
      const block = buffer.slice(0, separatorIndex);
      buffer = buffer.slice(separatorIndex + 2);
      if (block.length > 0) {
        terminalEventReceived = dispatchEvent(block);
        if (terminalEventReceived) break;
      }
      separatorIndex = buffer.indexOf('\n\n');
    }
  }

  if (terminalEventReceived) {
    try {
      await reader.cancel();
    } catch (error) {
      console.warn('Unable to close completed SSE stream', error);
    }
    return;
  }

  // Flush any trailing block (servers should end with \n\n, but be defensive).
  if (buffer.trim().length > 0) dispatchEvent(buffer);
};
